import type { HubConnection } from '@microsoft/signalr';

export const SIGNALR_TIMEOUT_MESSAGE = 'Server timeout elapsed without receiving a message from the server.';
export const SIGNALR_CONNECTION_CLOSED_MESSAGE = 'Invocation canceled due to the underlying connection being closed.';
export const SIGNALR_SERVER_TIMEOUT_MS = 60_000;
export const SIGNALR_KEEP_ALIVE_INTERVAL_MS = 20_000;

type SignalRErrorLike = {
  error?: unknown;
  message?: unknown;
  reason?: unknown;
};

function extractSignalRErrorMessage(error: unknown): string | null {
  if (!error) {
    return null;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object') {
    const { error: nestedError, message, reason } = error as SignalRErrorLike;

    return (
      extractSignalRErrorMessage(message) ??
      extractSignalRErrorMessage(reason) ??
      extractSignalRErrorMessage(nestedError)
    );
  }

  return null;
}

function hasSignalRErrorMessage(error: unknown, expectedMessage: string) {
  const message = extractSignalRErrorMessage(error);

  return typeof message === 'string' && message.includes(expectedMessage);
}

export function configureSignalRConnection(connection: HubConnection) {
  connection.serverTimeoutInMilliseconds = SIGNALR_SERVER_TIMEOUT_MS;
  connection.keepAliveIntervalInMilliseconds = SIGNALR_KEEP_ALIVE_INTERVAL_MS;
}

export function isExpectedSignalRError(error: unknown) {
  return hasSignalRErrorMessage(error, SIGNALR_TIMEOUT_MESSAGE);
}

export function isSignalRConnectionClosedError(error: unknown) {
  return hasSignalRErrorMessage(error, SIGNALR_CONNECTION_CLOSED_MESSAGE);
}

export function logSignalRError(scope: string, error?: unknown) {
  if (!error) {
    return;
  }

  if (isExpectedSignalRError(error) || isSignalRConnectionClosedError(error)) {
    console.warn(`[SignalR] ${scope}: ${extractSignalRErrorMessage(error)}`);
    return;
  }

  console.error(`[SignalR] ${scope}`, error);
}

export async function sendSignalRMessage(
  connection: HubConnection | null | undefined,
  scope: string,
  methodName: string,
  ...args: unknown[]
) {
  if (!connection) {
    return false;
  }

  try {
    await connection.send(methodName, ...args);
    return true;
  } catch (error) {
    logSignalRError(scope, error);
    return false;
  }
}
